import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { rxResource } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { BulkEditSectionFormService, MultiSelect, ZagIcon, type DropdownOption } from '@zag/ui';
import type { ManagedRole } from '@zag/auth/models';

const SECTION_ID = 'bulk-roles';

type Mode = 'add' | 'remove';

interface State {
  roleIds: string[];
  mode: Mode;
}

@Component({
  selector: 'sc-person-roles-bulk-section',
  template: `
    <div class="sc-roles-bulk-section">
      <div class="sc-roles-bulk-section__mode-toggle">
        <button
          type="button"
          class="sc-roles-bulk-section__mode-option"
          [class.sc-roles-bulk-section__mode-option--active]="state().mode === 'add'"
          (click)="setMode('add')"
        >
          <span zagIcon="add"></span>
          <span>{{ 'bulkEdit.users.roles.modeAdd' | translate }}</span>
        </button>
        <button
          type="button"
          class="sc-roles-bulk-section__mode-option"
          [class.sc-roles-bulk-section__mode-option--active]="state().mode === 'remove'"
          (click)="setMode('remove')"
        >
          <span zagIcon="remove"></span>
          <span>{{ 'bulkEdit.users.roles.modeRemove' | translate }}</span>
        </button>
      </div>
      <zag-multi-select
        [options]="roleOptions()"
        [placeholder]="'bulkEdit.users.roles.selectRoles' | translate"
        [searchable]="true"
        [searchPlaceholder]="'bulkEdit.users.roles.searchRoles' | translate"
        [noResultsText]="'bulkEdit.users.roles.noRoles' | translate"
        [ngModel]="state().roleIds"
        (ngModelChange)="onRolesChange($event)"
      />
    </div>
  `,
  styles: [
    `
      .sc-roles-bulk-section {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md);
        width: 100%;
      }

      .sc-roles-bulk-section__mode-toggle {
        display: flex;
        align-items: center;
        padding: var(--spacing-xs);
        background: var(--surface-color-secondary);
        border-radius: var(--border-radius-md);
      }

      .sc-roles-bulk-section__mode-option {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--spacing-sm);
        flex: 1;
        padding: var(--spacing-sm);
        background: transparent;
        border: none;
        border-radius: var(--border-radius-sm);
        color: var(--text-color-secondary);
        font-size: var(--font-size-sm);
        font-weight: 500;
        cursor: pointer;
        transition: all var(--transition-duration-fast);

        &:hover {
          color: var(--text-color-primary);
        }

        span[zagIcon] {
          font-size: 18px;
        }

        &.sc-roles-bulk-section__mode-option--active {
          background: var(--background-color-elevated);
          color: var(--text-color-primary);
          box-shadow: var(--shadow-sm);
          cursor: default;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MultiSelect, FormsModule, TranslatePipe, ZagIcon],
})
export class PersonRolesBulkSectionComponent implements OnInit, OnDestroy {
  private readonly formService = inject(BulkEditSectionFormService);
  private readonly http = inject(HttpClient);

  protected readonly state = signal<State>({ roleIds: [], mode: 'add' });

  private readonly rolesResource = rxResource({
    stream: () => this.http.get<ManagedRole[]>('/api/v1/rbac/roles'),
  });

  protected readonly roleOptions = computed<DropdownOption<string>[]>(() =>
    (this.rolesResource.value() ?? []).map(r => ({ value: r.id, label: r.id }))
  );

  protected setMode(mode: Mode): void {
    this.state.update(s => ({ ...s, mode }));
  }

  protected onRolesChange(roleIds: string[]): void {
    this.state.update(s => ({ ...s, roleIds }));
  }

  ngOnInit(): void {
    this.formService.register(SECTION_ID, () => this.state());
  }

  ngOnDestroy(): void {
    this.formService.unregister(SECTION_ID);
  }
}
